/* eslint-disable operator-assignment */

import { fromJS } from "immutable";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";

import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { ArrangeListHeading, ArrangeListItemStyled, ArrangePopupStyled, RadiusLink, RadiusPopupStyled, ReframeButtonContainer, ReframeHeader, ReframeLayouContainer, ReframeModalContainer, TransformButton, TransformFlipContainer, TransformHeading, TransformPopupStyled, TransformRotateContainer, TransparencyPopupStyled } from "./itemtoolbar-components";
import content from "../../constants/content";
import { applyFlip, bulkObjectUpdate, moveUpdate, updateProjectData, updateTimelineTime } from "../../redux/actions/timelineUtils";
import NumberInput from "../../common-components/NumberInputComponent";
import Slider from "../zoom/zoom-slider";
import { STATIC_PATH, REFRAME_DOMAIN } from "../../constants/config";
import { alignBottom, alignCenter, alignHorizontally, alignLeft, alignMiddle, alignRight, alignTidyUp, alignTop, alignVertically, getObjectsBounds, getRadiusClipPath } from "../../helper/TransformManagerHelper";
import { handleLayerPosition, isImageOnly, isVideoOnly, randomString } from "../timeline/timeline-helper";
import { getSelectedItemIds, getSelectionContainer } from "../../helper/IndividualSelectHelper";
import { flipPositionToState, flipStateToPosition, getNextRotation, rotationDisplayValue } from "./itemtoolbar-helper";
import { getAiReframe, reframeStatusApi, setReframeData, setReframeMode, setSelectedItems, updateImperativeRotate } from "../../redux/actions/appUtils";
import vmTheme from "../../constants/theme";
import { singleClipFrameToFrame } from "../frame/frame-helper";
import { font } from "../../constants/font";
import { Modal } from "../../common-components/modal";
import { PORTAL_ID } from "../../constants";
import ReframeProgressModal from "../../common-components/ReframeProgressModal";
import ErrorModal from "../../common-components/ErrorModal";
import { createNewReframe, createNewScene } from "../../helper/reframeHelper";
import Typography from "../../common-components/Typography";
import Action from "../../common-components/Action";

export const TransparencyPopup = () => {
    const _selectedItems = useSelector((state) => state.app.get("selectedItems"));
    const originalOpacity = useSelector((state) => {
        const selectedItems = state.app.get("selectedItems");
        const workspaceItems = state.projectDetails.get("workspaceItems");
        const firstItem = workspaceItems.get(selectedItems.first());

        if (!firstItem) {
            return 0;
        }

        return selectedItems.reduce(
            (prevOpacity, itemId) => {
                const itemOpacity = workspaceItems.getIn([itemId, "opacity"]);
                return prevOpacity < itemOpacity ? prevOpacity : itemOpacity
            },
            firstItem.get("opacity")
        );
    });
    const theme = useSelector((state) => state.app.get('theme'));
    const dispatch = useDispatch();
    const intl = useIntl();
    const [opacity, setOpacity] = useState(originalOpacity);
    const [selectedItems, setSelectedItems] = useState(_selectedItems);

    const onChange = (e, newOpacity) => {
        selectedItems.valueSeq().forEach((itemId) => {
            const itemEl = document.getElementById(itemId);
            if (itemEl) {
                itemEl.style.opacity = newOpacity / 100;
            }
        });
        setOpacity(newOpacity / 100);
    }

    const onSubmit = () => {
        // do not update internal state here as number input component will trigger this function on unmount!
        if (originalOpacity !== opacity) {
            const toUpdate = {};
            selectedItems.forEach((itemId) => {
                toUpdate[itemId] = {
                    data: { opacity },
                };
            })
            dispatch(bulkObjectUpdate({ workspaceItems: toUpdate }));
        }
    }

    useEffect(() => {
        setOpacity(originalOpacity);
    }, [originalOpacity]);

    // to delay selection change for submit on unmount
    useEffect(() => {
        setSelectedItems(_selectedItems);
    }, [_selectedItems])

    const displayValue = Math.round(opacity * 1000) / 10;
    return (
        <TransparencyPopupStyled>
            <p className="item-tool-tps--heading">
                {intl.formatMessage({ id: content.TRANSPARENCY })}
            </p>
            <NumberInput
                min={0}
                max={100}
                step={0.5}
                value={displayValue}
                precision={1}
                onChange={onChange}
                onSubmit={onSubmit}
                suffix="%"
                saveOnUnmount={true}
            />
            <div className="item-tool-tps--slider">
                <Slider
                    min={0}
                    max={100}
                    inputMin={0}
                    inputMax={100}
                    value={displayValue}
                    step={0.5}
                    onChangeSliderValue={onChange}
                    onMouseUp={onSubmit}
                    border={"none"}
                    borderRadius={"3px"}
                    background={vmTheme[theme].veryLightGray}
                    margin={"0"}
                    height={"5px"}
                    thumb={{
                        background: `${vmTheme[theme].polarColor} 0% 0% no-repeat padding-box`,
                        border: `2px solid ${vmTheme[theme].secondaryBorderColor}`,
                        height: "16px",
                        width: "16px",
                        hoverBG: `${vmTheme[theme].secondaryBorderColor}`,
                        hoverBorder: `1.7px solid ${vmTheme[theme].secondaryBorderColor}`,
                    }}
                    progressBackground={vmTheme[theme].secondaryBorderColor}
                    isChangeBg={true}
                />
            </div>
        </TransparencyPopupStyled>
    );
}

export const ReframeModal = () => {
    const selectedItems = useSelector((state) => state.app.get("selectedItems"));
    const selectedSubtitles = useSelector((state) => state.app.get("selectedSubtitles"));
    const selectedItemId = selectedItems.first();
    const isReframeModelOpen = useSelector((state) => state.app.get("isReframeMode"));
    const projectWidth = useSelector((state) => state.app.get("projectWidth"));
    const projectHeight = useSelector((state) => state.app.get("projectHeight"));
    const [reframeProgress, setReframeProgress] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [errorModal, setErrorModal] = useState({
        show: false,
        title: "",
        message: "",
        onRetry: null,
    });
    const theme = useSelector((state) => state.app.get("theme"));
    const { userId, teamId } = useSelector((state) => state.userDetails) || {};
    const cancelReframeRef = useRef(false);
    const reframeErrorShownRef = useRef(false);

    const item = useSelector((state) => {
        return state.projectDetails.getIn([
            "workspaceItems",
            selectedItemId
        ]);
    });

    // Check if any selected subtitle (scene clip) has sceneData or layout, which indicates it's already reframed
    const hasReframedSceneClip = useSelector((state) => {
        if (!selectedSubtitles || selectedSubtitles.size === 0) return false;

        const workspaceItems = state.projectDetails.get("workspaceItems");
        return selectedSubtitles.some((subtitleId) => {
            const sceneItem = workspaceItems.get(subtitleId) ||
                workspaceItems.find(item => item.get("sceneId") === subtitleId);
            return sceneItem?.get("sceneData") || sceneItem?.get("layout");
        });
    });
    
    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            cancelReframeRef.current = true;
        };
    }, []);

    const closeErrorModal = useCallback(() => {
        setErrorModal({ show: false, title: "", message: "", onRetry: null });
    }, []);

    const handleGenerateAIFrameRef = useRef(null);

    const showReframeErrorModal = useCallback((res) => {
        if (reframeErrorShownRef.current) return;
        reframeErrorShownRef.current = true;
        setShowProgressModal(false);
        setIsGenerating(false);
        setReframeProgress(0);

        const code = res?.code;
        const errorByCode = {
            RF001: { title: content.RF001_TITLE, message: content.RF001 },
            RF002: { title: content.RF002_TITLE, message: content.RF002 },
            RF003: { title: content.RF003_TITLE, message: content.RF003 },
            RF004: { title: content.RF004_TITLE, message: content.RF004 },
            RF005: { title: content.RF005_TITLE, message: content.RF005 },
            RF006: { title: content.RF006_TITLE, message: content.RF006 },
        };
        const { title, message } = errorByCode[code] || {
            title: content.RF001_TITLE,
            message: content.RF001,
        };

        setErrorModal({
            show: true,
            title,
            message,
            onRetry: code === "RF002"
                ? () => {
                    closeErrorModal();
                    handleGenerateAIFrameRef.current?.();
                }
                : null,
        });
    }, [closeErrorModal]);

    const pollReframeStatus = async (jobId, retryCount = 0, maxRetries = 3000) => {
        if (cancelReframeRef.current) return false;
        try {
            const res = await reframeStatusApi(jobId);
            if (cancelReframeRef.current) return false;

            if (res?.error) {
                showReframeErrorModal(res);
                return false;
            }

            setReframeProgress(res.progress);
            if (res.status === "COMPLETED" || res.status === "Completed") {
                setShowProgressModal(false);
                return res;
            }

            if (res.status === "Failed" || res.status === "FAILED") {
                showReframeErrorModal(res);
                return false;
            }

            if (retryCount < maxRetries) {
                return new Promise(resolve => {
                    setTimeout(() => {
                        resolve(pollReframeStatus(jobId, retryCount + 1, maxRetries));
                    }, 3000);
                });
            }

            showReframeErrorModal(res || { code: "RF001" });
            return false;
        } catch (error) {
            if (cancelReframeRef.current) return false;
            if (retryCount < maxRetries) {
                return new Promise(resolve => {
                    setTimeout(() => {
                        resolve(pollReframeStatus(jobId, retryCount + 1, maxRetries));
                    }, 2000);
                });
            }
            showReframeErrorModal(error || { code: "RF001" });
            return false;
        }
    };

    const handleCloseModal = () => {
        dispatch(setReframeMode(false));
    };

    const handleReframeData = async (url) => {
        const response = await fetch(url);
        const resaa = await response.json();

        if (resaa && typeof resaa === "object") {
            resaa.assetId = item.get("assetId");
        }

        const toUpdate = await createNewReframe(item, resaa, projectWidth, projectHeight);
        const selectedItemUpdate = [
            {
                id: selectedItemId,
                container: "workspaceItems",
                toUpdate: {
                    isReframe: true,
                    // Preserve the item's existing dropId. Subtitles are keyed by dropId in
                    // subtitle.data, and buildLocalSubtitles maps them onto the (non-scene)
                    // reframe-parent item via item.dropId. Overwriting dropId with the item's
                    // own id breaks that lookup and drops the subtitles when reframe is re-applied.
                    dropId: item.get("dropId") || selectedItemId,
                    reframeSrc: resaa
                },
            },
        ];
        dispatch(updateTimelineTime({ toUpdate: [...selectedItemUpdate, ...toUpdate] }));
        const dropId = item.get("dropId");
        if (dropId) {
            const [shortId] = dropId.split("_");
            if (shortId && shortId.startsWith("short")) {
                const compositeRes = {
                    ...resaa,
                    assetId: `${item.get("assetId")}_${shortId}`
                };
                dispatch(setReframeData(compositeRes));
            }
        }
        dispatch(setReframeData(resaa));
        // Clear reframe mode after AI Reframe is applied
        dispatch(setReframeMode(false));
        // Select the first scene's items in workspace (handles split/three/four layouts)
        const workspaceItems = toUpdate.filter(({ container, isDelete, newItemData }) => container === "workspaceItems" && !isDelete && newItemData);
        const firstSceneId = workspaceItems[0]?.newItemData?.sceneId ?? null;
        const firstSceneItemIds = firstSceneId ?
        workspaceItems.filter(({ newItemData }) => newItemData?.sceneId === firstSceneId).map(({ id }) => id) : [];
        dispatch(setSelectedItems({ selectedItems: firstSceneItemIds, selectedSubtitles: [], selectedAudios: [] }));
    }

    const handleCancelProgress = () => {
        cancelReframeRef.current = true;
        setShowProgressModal(false);
        setIsGenerating(false);
        setReframeProgress(0);
        handleCloseModal();
    };

    const handleGenerateAIFrame = async () => {
        setIsGenerating(true);
        cancelReframeRef.current = false;
        reframeErrorShownRef.current = false;
        try {
            const payload = {
                assetId: item.get("assetId"),
                userId,
                teamId,
            };
            const extractReframeSrc = (data) => {
                if (!data) return null;
                let src = null;
                if (data.reframeSrc) {
                    src = data.reframeSrc;
                } else if (Array.isArray(data.clips) && data.clips[0] && data.clips[0].reframeSrc) {
                    src = data.clips[0].reframeSrc;
                } else if (data.reframe) {
                    if (data.reframe.reframeSrc) {
                        src = data.reframe.reframeSrc;
                    } else if (Array.isArray(data.reframe.clips) && data.reframe.clips[0] && data.reframe.clips[0].reframeSrc) {
                        src = data.reframe.clips[0].reframeSrc;
                    }
                }
                if (src && typeof src === "string" && !src.startsWith("http")) {
                    const base = REFRAME_DOMAIN.endsWith("/") ? REFRAME_DOMAIN.slice(0, -1) : REFRAME_DOMAIN;
                    const path = src.startsWith("/") ? src : `/${src}`;
                    return `${base}${path}`;
                }
                return src;
            };

            const reframeData = await getAiReframe(payload);
            if (cancelReframeRef.current) return;

            if (reframeData?.error) {
                showReframeErrorModal(reframeData);
                return;
            }

            if (reframeData?.status === "Queued" || reframeData?.status === "QUEUED" || reframeData?.status === "IN_PROGRESS") {
                setShowProgressModal(true);
                const completedData = await pollReframeStatus(reframeData.job_id);
                if (cancelReframeRef.current) return;
                if (!completedData) {
                    if (!reframeErrorShownRef.current) {
                        showReframeErrorModal(completedData || { code: "RF001" });
                    }
                    return;
                }
                const src = extractReframeSrc(completedData);
                if (!src) {
                    showReframeErrorModal({ code: "RF001" });
                    return;
                }
                handleReframeData(src);
            } else if (reframeData?.status === "Completed" || reframeData?.status === "COMPLETED") {
                const src = extractReframeSrc(reframeData);
                if (!src) {
                    showReframeErrorModal({ code: "RF001" });
                    return;
                }
                handleReframeData(src);
            } else if (reframeData?.status === "Failed" || reframeData?.status === "FAILED") {
                showReframeErrorModal(reframeData);
                return;
            }
            handleCloseModal();
        } catch (error) {
            showReframeErrorModal(error || { code: "RF001" });
            console.error("Error generating AI reframe:", error);
        } finally {
            setIsGenerating(false);
        }
    };
    handleGenerateAIFrameRef.current = handleGenerateAIFrame;

    return (
        <>
            {errorModal.show && (
                <ErrorModal
                    title={errorModal.title}
                    message={errorModal.message}
                    primaryButtonText={errorModal.onRetry ? content.TRY_AGAIN : undefined}
                    secondaryButtonText={content.CLOSE}
                    onPrimaryButtonClick={errorModal.onRetry ? errorModal.onRetry : undefined}
                    onSecondaryButtonClick={closeErrorModal}
                    onCloseModal={closeErrorModal}
                />
            )}
            <Modal
                width="584px"
                height="464px"
                showModal={isReframeModelOpen && !item?.get("isReframe") && !item?.get("sceneData") && !hasReframedSceneClip && selectedItems.size > 0 && !showProgressModal && !errorModal.show}
                onClose={handleCloseModal}
                className="upload--modal"
                isRender={true}
                borderRadius="16px"
            >
                <img alt="reframesample" src={`${STATIC_PATH}reframesample.svg`} />
                <ReframeModalContainer>
                    <ReframeHeader>
                        <Typography
                            content={content.AI_REFRAME}
                            display="block"
                            padding="2px 0"
                            color="#313345"
                            font={font.bold_20_26}
                            cursor="text"
                        />
                        <Typography
                            content={content.REFRAME_DESCRIPTION}
                            display="block"
                            padding="2px 0"
                            color="#313345"
                            font={font.normal_21}
                            enableTrim={false}
                            cursor="text"
                            align={"center"}
                        />
                    </ReframeHeader>
                    <ReframeButtonContainer>
                        <Action
                            className="contact-btn"
                            onClick={() => handleGenerateAIFrame()}
                            borderRadius="12px"
                            text={content.GENERATE_REFRAME}
                            marginTop="24px"
                            marginBottom={'0px'}
                            font={font.bold_19}
                            customBeforeIcon={`${STATIC_PATH}reframePath.svg`}
                            iconLeft={"139px"}
                            isLoading={isGenerating}
                            fontColor="#FFFFFF"
                            height="48px"
                            width="100%"
                            background={vmTheme[theme].aiFrameBgColor || "linear-gradient(91deg, #AC67FD 0%, #775AFD 100%)"}
                            hoverColor={vmTheme[theme].hoverAiFrameBgColor || "linear-gradient(91deg, #775AFD 0%, #AC67FD 100%)"}
                        />
                    </ReframeButtonContainer>
                </ReframeModalContainer>
            </Modal>
            <ReframeProgressModal
                progress={reframeProgress}
                status="processing"
                message="Generating AI Reframe..."
                show={showProgressModal}
                onClose={handleCancelProgress}
            />
        </>
    );
};


export const ReframeLayout = ({ positionEvent, closeReframeLayout, usePortal }) => {
    const dispatch = useDispatch();
    const selectedValue = useSelector((state) => state.app.get("selectedSubtitles"));
    const wItems = useSelector((state) => state.projectDetails.get("workspaceItems"));
    const projectWidth = useSelector((state) => state.app.get("projectWidth"));
    const projectHeight = useSelector((state) => state.app.get("projectHeight"));
    const project = useSelector((state) => state.projectDetails);
    const appReducer = useSelector((state) => state.app);
    const targetItem = wItems.get(selectedValue.get(0)) || wItems.find(item => item.get("sceneId") === selectedValue.get(0));
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const optionRef = useRef(null);

    useLayoutEffect(() => {
        if (optionRef.current && positionEvent) {
            const optionBound = optionRef.current.getBoundingClientRect();
            setPosition({
                x: Math.max(10, positionEvent.x - (optionBound.width / 2)),
                // Left Cannot be negative number
                y: positionEvent.y - optionBound.height,
            });
        }
    }, [positionEvent]);

    const handleOutsideClick = useCallback((e) => {
        const preventClosing = optionRef.current && optionRef.current.contains(e.target);
        if (!preventClosing && typeof closeReframeLayout === "function") {
            closeReframeLayout();
        }
    }, [closeReframeLayout]);

    useEffect(() => {
        window.addEventListener("pointerdown", handleOutsideClick);
        return () => {
            window.removeEventListener("pointerdown", handleOutsideClick);
        };
    }, [handleOutsideClick]);


    const createLayoutSceneData = (layoutType, itemOriginalScene, selectedPersonKey) => {
        const coordinates = itemOriginalScene.coordinates || {};
        const personKeys = Object.keys(coordinates);
        const newSceneData = { ...itemOriginalScene };

        // Layout configuration mapping
        const layoutConfig = {
            fill: { requiredPersons: 1, layoutName: "FILL" },
            fit: { requiredPersons: 1, layoutName: "FIT" },
            split: { requiredPersons: 2, layoutName: "SPLIT" },
            three: { requiredPersons: 3, layoutName: "THREE" },
            four: { requiredPersons: 4, layoutName: "FOUR" },
            // screenshare: { requiredPersons: 2, layoutName: "SCREENSHARE" },
            // gameplay: { requiredPersons: 2, layoutName: "GAMEPLAY" }
        };

        const config = layoutConfig[layoutType.toLowerCase()];
        if (!config) return false;

        const newCoordinates = { ...coordinates };

        if (config.requiredPersons === 1) {
            // Use the selected person's coordinates for single-person layouts (FILL, FIT)
            const personToUse = selectedPersonKey && coordinates[selectedPersonKey] 
                ? selectedPersonKey 
                : (coordinates.person_1 ? "person_1" : personKeys[0]);
            newCoordinates.person_1 = coordinates[personToUse];
            // Remove other persons
            Object.keys(newCoordinates).forEach(key => {
                if (key !== "person_1") {
                    delete newCoordinates[key];
                }
            });
        } else {
            for (let i = 1; i <= config.requiredPersons; i++) {
                const personKey = `person_${i}`;
                // Only create if person doesn't exist
                if (!coordinates[personKey]) {
                    // Use the last existing person as template for duplication
                    const lastPersonKey = `person_${personKeys.length}`;
                    const templateData = coordinates[lastPersonKey] || coordinates[personKeys[0]];
                    newCoordinates[personKey] = templateData;
                }
            }
        }

        newSceneData.coordinates = newCoordinates;
        newSceneData.layout = config.layoutName;
        return newSceneData;
    };

    const handleReframeLayout = (layoutType) => {
        const selectedSceneKey = selectedValue.get(0);
        const itemDirect = wItems.get(selectedSceneKey);
        const itemBySceneId = !itemDirect ? wItems.find(workspaceItem => workspaceItem.get("sceneId") === selectedSceneKey) : null;
        const item = itemDirect || itemBySceneId;

        if (!item) return;
        const currentScene = item.get("sceneData");
        if (!currentScene) return;
        const filteredKeys = [];
        const toUpdates = [];

        const selectedOriginalId = item.get("originalId") || item.get("dropId") || item.get("id");
        wItems.forEach((workspaceItem, itemId) => {
            const itemOrigId = workspaceItem.get("originalId") || workspaceItem.get("dropId") || workspaceItem.get("id");
            if (
                workspaceItem.get("sceneId") === item.get('sceneId') &&
                itemOrigId === selectedOriginalId
            ) {
                filteredKeys.push(itemId);
            }
        });

        filteredKeys.forEach(id => {
            toUpdates.push(
                {
                    id,
                    container: "workspaceItems",
                    isDelete: true
                },
            )
        })

        // Use the originally selected item, not filteredKeys[0]
        const selectedSceneItem = item;
        if (!selectedSceneItem) return;

        const assetId = selectedSceneItem.getIn(["assetId"]);
        const itemSceneId = selectedSceneItem.getIn(["sceneData", "scene_id"]);
        const originalId = selectedSceneItem.get("originalId") || selectedSceneItem.get("dropId") || selectedSceneItem.get("id");
        const originalItem = project.getIn(["workspaceItems", originalId]) || selectedSceneItem;
        const scenes = project.getIn(["reframeData", assetId, "scenes"]);
        const appReframeData = appReducer.getIn(["reframeData", assetId, "scenes"]) || appReducer.getIn(["reframeData", "scenes"]);
        let sceneData;
        let reframeData;
        if (scenes && scenes.size > 0) {
            sceneData = scenes;
            reframeData = project.getIn(["reframeData", assetId]);
        } else if (appReframeData && (appReframeData.size > 0 || appReframeData.length > 0)) {
            sceneData = appReframeData.size ? appReframeData : fromJS(appReframeData);
            reframeData = appReducer.getIn(["reframeData", assetId]) || appReducer.getIn(["reframeData"]);
        }
        const scenesJS = sceneData ? sceneData.toJS() : null;
        if (!scenesJS) return;

        const index = scenesJS.findIndex(scene => scene.scene_id === itemSceneId);
        const itemOriginalScene = scenesJS[index];

        if (!itemOriginalScene) {
            console.error("itemOriginalScene not found for", itemSceneId);
            if (typeof closeReframeLayout === "function") {
                closeReframeLayout();
            }
            return;
        }

        // Get the selected person's key from the selected item's sceneData
        const selectedPersonKey = selectedSceneItem.getIn(["sceneData", "personKey"]);
        const newSceneData = createLayoutSceneData(layoutType, itemOriginalScene, selectedPersonKey);
        if (!newSceneData) return;

        // For split scenes, override timing to match the actual split item's range
        // Only use this locally for createNewScene positioning — do NOT persist to reframeData
        const itemVideoStart = selectedSceneItem.get("videoStart");
        const itemVideoEnd = selectedSceneItem.get("videoEnd");
        const localSceneData = { ...newSceneData };
        if (itemVideoStart != null && itemVideoEnd != null) {
            localSceneData.start_time = itemVideoStart;
            localSceneData.end_time = itemVideoEnd;
        }

        const toUpdate = createNewScene(localSceneData, originalItem, reframeData, index, sceneData.size, projectWidth, projectHeight);
        const selectedSceneId = selectedSceneItem.get("sceneId");
        toUpdate.forEach(updateItem => {
            updateItem.isDontSelect = true;
            // Preserve the sceneId from the selected item so split identity is maintained
            updateItem.newItemData.sceneId = selectedSceneId;
            // Make item IDs unique per split portion to avoid conflicts
            const uniqueId = `${selectedSceneId}-newreframe${originalId}-${updateItem.newItemData.layout || layoutType}${randomString()}`;
            updateItem.id = uniqueId;
            updateItem.newItemData.id = uniqueId;
            updateItem.newItemData.drop = uniqueId;
        });
        if (typeof closeReframeLayout === "function") {
            closeReframeLayout();
        }

        const newItemIds = toUpdate.map(updateItem => updateItem.id);

        const isSplitClip = wItems.some((wsItem, wsId) =>
            wsId !== originalId &&
            wsItem.get("isReframe") === true &&
            wsItem.get("assetId") === assetId &&
            !wsItem.get("sceneData")
        );

        if (!isSplitClip) {
            const persistedSceneData = { ...newSceneData };
            // Preserve original timing from itemOriginalScene (covers the full unsplit range)
            persistedSceneData.start_time = itemOriginalScene.start_time;
            persistedSceneData.end_time = itemOriginalScene.end_time;

            const currentAppReframeData = appReducer.get("reframeData");
            if (currentAppReframeData) {
                let updatedReframeData = currentAppReframeData;
                if (currentAppReframeData.hasIn([assetId, "scenes"])) {
                    updatedReframeData = currentAppReframeData.setIn([assetId, "scenes", index], fromJS(persistedSceneData));
                } else if (currentAppReframeData.has("scenes")) {
                    updatedReframeData = currentAppReframeData.setIn(["scenes", index], fromJS(persistedSceneData));
                }
                dispatch(setReframeData(updatedReframeData.toJS()));
            }

            const currentProjectReframeData = project.get("reframeData");
            if (currentProjectReframeData) {
                let updatedProjectReframeData = currentProjectReframeData;
                if (currentProjectReframeData.hasIn([assetId, "scenes"])) {
                    updatedProjectReframeData = currentProjectReframeData.setIn([assetId, "scenes", index], fromJS(persistedSceneData));
                } else if (currentProjectReframeData.has("scenes")) {
                    updatedProjectReframeData = currentProjectReframeData.setIn(["scenes", index], fromJS(persistedSceneData));
                }
                dispatch(updateProjectData({ reframeData: updatedProjectReframeData.toJS() }));
            }
        }

        dispatch(updateTimelineTime({ toUpdate: [...toUpdates, ...toUpdate] }));
        dispatch(setSelectedItems({ selectedItems: newItemIds, selectedSubtitles: newItemIds }));
    };

    const layoutList = [
        {
            langId: "fill",
            defaultMessage: "Fill",
            src: `${STATIC_PATH}fill.svg`,
            active: `${STATIC_PATH}fill-active.svg`,
            layoutName: "FILL"
        },
        {
            langId: "fit",
            defaultMessage: "Fit",
            src: `${STATIC_PATH}fit.svg`,
            active: `${STATIC_PATH}fit-active.svg`,
            layoutName: "FIT"
        },
        {
            langId: "split",
            defaultMessage: "Split",
            src: `${STATIC_PATH}frameLayout.svg`,
            active: `${STATIC_PATH}frameLayout-active.svg`,
            layoutName: "SPLIT"
        },
        {
            langId: "three",
            defaultMessage: "Three",
            src: `${STATIC_PATH}three-frame.svg`,
            active: `${STATIC_PATH}three-frame-active.svg`,
            layoutName: "THREE"
        },
        {
            langId: "four",
            defaultMessage: "Four",
            src: `${STATIC_PATH}four-frame.svg`,
            active: `${STATIC_PATH}four-frame-active.svg`,
            layoutName: "FOUR"
        },
        // {
        //     langId: "screenshare",
        //     defaultMessage: "ScreenShare",
        //     src: `${STATIC_PATH}cast.svg`,
        //     active: `${STATIC_PATH}cast-active.svg`,
        //     layoutName: "SCREENSHARE"
        // },
        // {
        //     langId: "gameplay",
        //     defaultMessage: "GamePlay",
        //     src: `${STATIC_PATH}gameplay.svg`,
        //     active: `${STATIC_PATH}gameplay-active.svg`,
        //     layoutName: "GAMEPLAY"
        // },
    ]

    const portalRoot = document.getElementById(PORTAL_ID);

    const layoutPopup = (
        <>
            <ReframeLayouContainer
                className="reframe-layout-popup"
                ref={optionRef}
                style={positionEvent ? {
                    position: 'fixed',
                    left: position.x,
                    top: position.y,
                    zIndex: 1000
                } : undefined}
            >
                <ul style={{ padding: 0, margin: 0 }}>
                    {
                        layoutList.map((layout) => (
                            <ArrangeListItem
                                key={layout.langId}
                                langId={layout.langId}
                                defaultMessage={layout.defaultMessage}
                                imgSrc={layout.src}
                                activeImgSrc={layout.active}
                                hoverImgSrc={layout.active}
                                onClick={() => handleReframeLayout(layout.langId)}
                                imgWidth={"20px"}
                                font={font.normalBase}
                                selectedItem={targetItem && targetItem?.get("layout") === layout.layoutName}
                            />
                        ))
                    }
                </ul>
            </ReframeLayouContainer>
            <div className="backdrop" />
        </>
    )


    return usePortal && portalRoot
        ? ReactDOM.createPortal(layoutPopup, portalRoot)
        : layoutPopup;
}

ReframeLayout.propTypes = {
    positionEvent: PropTypes.func,
    closeReframeLayout: PropTypes.func,
    usePortal: PropTypes.bool,
};


const ArrangeListItem = (props) => {
    let { className = "" } = props;
    const { isDisabled, imgSrc, hoverImgSrc, disabledImgSrc, activeImgSrc } = props;

    const intl = useIntl();
    const msg = intl.formatMessage({ id: props.langId, defaultMessage: props.defaultMessage });

    const style = {};

    if (imgSrc) {
        style["--it-popup-src"] = `url(${imgSrc})`;
    }
    if (hoverImgSrc) {
        style["--it-popup-hoverSrc"] = `url(${hoverImgSrc})`;
    }
    if (disabledImgSrc) {
        style["--it-popup-disabledSrc"] = `url(${disabledImgSrc})`;
    }
    if (activeImgSrc) {
        style["--it-popup-activeSrc"] = `url(${activeImgSrc})`;
    }
    if (isDisabled) {
        className = `${className} tool-disabled`;
    }
    if (props.selectedItem) {
        className = `${className} selected`;
    }

    return (
        <ArrangeListItemStyled
            className={className}
            onClick={props.onClick}
            style={style}
            width={props.imgWidth}
            font={props.font}
            selectedItem={props.selectedItem}
        >
            <img draggable="false" alt={msg} src={props.selectedItem ? (activeImgSrc || imgSrc) : imgSrc} />
            <p>{msg}</p>

        </ArrangeListItemStyled>
    );
}
ArrangeListItem.propTypes = {
    onClick: PropTypes.func,
    className: PropTypes.string,
    isDisabled: PropTypes.bool,
    imgSrc: PropTypes.string,
    hoverImgSrc: PropTypes.string,
    disabledImgSrc: PropTypes.string,
    activeImgSrc: PropTypes.string,
    langId: PropTypes.string,
    defaultMessage: PropTypes.string,
    imgWidth: PropTypes.string,
    font: PropTypes.string,
    selectedItem: PropTypes.bool,
};

export const LayerPopup = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const workspaceWidth = useSelector((state) => state.app.get("projectWidth"));
    const workspaceHeight = useSelector((state) => state.app.get("projectHeight"));
    const selectedItems = useSelector((state) => state.app.get("selectedItems"));
    const workspaceItems = useSelector((state) => state.projectDetails.get("workspaceItems"));
    const selectedItemId = selectedItems.first();
    const theme = useSelector((state) => state.app.get('theme'));

    const iconStatus = useMemo(() => {
        const currentItem = workspaceItems.get(selectedItemId);
        const status = {
            back: false,
            backward: false,
            front: false,
            forward: false,
        }

        if (currentItem) {
            const lastTrack = workspaceItems.reduce((prevTrack, item) => {
                return item.get("track") > prevTrack ? item.get("track") : prevTrack;
            }, 0);
            status.back = currentItem.get("track") > 0;
            status.backward = currentItem.get("track") > 0;
            status.front = currentItem.get("track") < lastTrack;
            status.forward = currentItem.get("track") < lastTrack;
        }

        return status;
    }, [workspaceItems, selectedItemId]);

    const handleTrackChange = (type) => {
        const toUpdate = handleLayerPosition({
            container: "workspaceItems",
            itemId: selectedItemId,
            items: workspaceItems,
            type,
        });
        if (toUpdate.length) {
            dispatch(updateTimelineTime({ toUpdate }))
        }
    }

    const handleAlign = (type) => {
        let alignedItems;
        const selectedItem = workspaceItems.get(selectedItemId);
        const selectionBounds = fromJS({ x: 0, y: 0, width: workspaceWidth, height: workspaceHeight });
        const selectedObjects = fromJS({ [selectedItemId]: selectedItem });

        if (type === "top") {
            alignedItems = alignTop(selectionBounds, selectedObjects);
        } else if (type === "middle") {
            alignedItems = alignMiddle(selectionBounds, selectedObjects);
        } else if (type === "bottom") {
            alignedItems = alignBottom(selectionBounds, selectedObjects);
        } else if (type === "left") {
            alignedItems = alignLeft(selectionBounds, selectedObjects);
        } else if (type === "center") {
            alignedItems = alignCenter(selectionBounds, selectedObjects);
        } else if (type === "right") {
            alignedItems = alignRight(selectionBounds, selectedObjects);
        }

        if (alignedItems) {
            dispatch(
                moveUpdate({
                    items: alignedItems,
                    currentContainer: "workspaceItems",
                    selectedItems,
                })
            );
        }
    }

    return (
        <ArrangePopupStyled className="layer-popup">
            <ul>
                {/* position menu */}
                <ArrangeListHeading className="layer-popup-item">
                    <p>{intl.formatMessage({ id: content.POSITION })}</p>
                </ArrangeListHeading>
                <ArrangeListItem
                    isDisabled={!iconStatus.forward}
                    langId={content.FORWARD}
                    imgSrc={`${STATIC_PATH}${vmTheme[theme].icons.forwardIcon}`}
                    hoverImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.forwardIcon}`}
                    disabledImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.forwardDisabledIcon}`}
                    onClick={() => handleTrackChange("forward")}
                />
                <ArrangeListItem
                    isDisabled={!iconStatus.backward}
                    langId={content.BACKWARD}
                    imgSrc={`${STATIC_PATH}${vmTheme[theme].icons.backwardIcon}`}
                    hoverImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.backwardIcon}`}
                    disabledImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.backwardDisabledIcon}`}
                    onClick={() => handleTrackChange("backward")}
                />
                <ArrangeListItem
                    isDisabled={!iconStatus.front}
                    langId={content.TO_FRONT}
                    imgSrc={`${STATIC_PATH}${vmTheme[theme].icons.frontIcon}`}
                    hoverImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.frontIcon}`}
                    disabledImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.frontDisabledIcon}`}
                    onClick={() => handleTrackChange("front")}
                />
                <ArrangeListItem
                    isDisabled={!iconStatus.back}
                    langId={content.TO_BACK}
                    imgSrc={`${STATIC_PATH}${vmTheme[theme].icons.backIcon}`}
                    hoverImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.backIcon}`}
                    disabledImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.backDisabledIcon}`}
                    onClick={() => handleTrackChange("back")}
                />

                {/* page align menu */}
                <ArrangeListHeading className="layer-popup-item">
                    <p>{intl.formatMessage({ id: content.ALIGN_TO_PAGE })}</p>
                </ArrangeListHeading>
                <ArrangeListItem
                    langId={content.TOP}
                    imgSrc={`${STATIC_PATH}${vmTheme[theme].icons.topAlignIcon}`}
                    hoverImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.topAlignIcon}`}
                    onClick={() => handleAlign("top")}
                />
                <ArrangeListItem
                    langId={content.LEFT}
                    imgSrc={`${STATIC_PATH}${vmTheme[theme].icons.leftAlignIcon}`}
                    hoverImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.leftAlignIcon}`}
                    onClick={() => handleAlign("left")}
                />
                <ArrangeListItem
                    langId={content.MIDDLE}
                    imgSrc={`${STATIC_PATH}${vmTheme[theme].icons.middleAlignIcon}`}
                    hoverImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.middleAlignIcon}`}
                    onClick={() => handleAlign("middle")}
                />
                <ArrangeListItem
                    langId={content.CENTER}
                    imgSrc={`${STATIC_PATH}${vmTheme[theme].icons.centerAlignIcon}`}
                    hoverImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.centerAlignIcon}`}
                    onClick={() => handleAlign("center")}
                />
                <ArrangeListItem
                    langId={content.BOTTOM}
                    imgSrc={`${STATIC_PATH}${vmTheme[theme].icons.bottomAlignIcon}`}
                    hoverImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.bottomAlignIcon}`}
                    onClick={() => handleAlign("bottom")}
                />
                <ArrangeListItem
                    langId={content.RIGHT}
                    imgSrc={`${STATIC_PATH}${vmTheme[theme].icons.rightAlignIcon}`}
                    hoverImgSrc={`${STATIC_PATH}${vmTheme[theme].icons.rightAlignIcon}`}
                    onClick={() => handleAlign("right")}
                />
            </ul>
        </ArrangePopupStyled>
    );
}

export const ArrangePopup = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const selectedItems = useSelector((state) => state.app.get("selectedItems"));
    const workspaceItems = useSelector((state) => state.projectDetails.get("workspaceItems"));

    const handleAlign = (type) => {
        let alignedItems;
        const selectedObjects = workspaceItems.filter((obj, key) => selectedItems.keyOf(key) !== undefined);
        const selectionBounds = fromJS(getObjectsBounds(selectedObjects));

        if (type === "top") {
            alignedItems = alignTop(selectionBounds, selectedObjects);
        } else if (type === "middle") {
            alignedItems = alignMiddle(selectionBounds, selectedObjects);
        } else if (type === "bottom") {
            alignedItems = alignBottom(selectionBounds, selectedObjects);
        } else if (type === "left") {
            alignedItems = alignLeft(selectionBounds, selectedObjects);
        } else if (type === "center") {
            alignedItems = alignCenter(selectionBounds, selectedObjects);
        } else if (type === "right") {
            alignedItems = alignRight(selectionBounds, selectedObjects);
        } else if (type === "vertically") {
            alignedItems = alignVertically(selectionBounds, selectedObjects);
        } else if (type === "tidyup") {
            alignedItems = alignTidyUp(selectionBounds, selectedObjects);
        } else if (type === "horizontally") {
            alignedItems = alignHorizontally(selectionBounds, selectedObjects);
        }

        if (alignedItems) {
            dispatch(
                moveUpdate({
                    items: alignedItems,
                    currentContainer: "workspaceItems",
                    selectedItems,
                })
            );
        }
    }

    return (
        <ArrangePopupStyled>
            <ul>
                {/* align items menu */}
                <ArrangeListHeading>
                    <p>{intl.formatMessage({ id: content.ALIGN_ELEMENTS })}</p>
                </ArrangeListHeading>
                <ArrangeListItem
                    langId={content.TOP}
                    imgSrc={`${STATIC_PATH}top-align.svg`}
                    hoverImgSrc={`${STATIC_PATH}top-align.svg`}
                    onClick={() => handleAlign("top")}
                />
                <ArrangeListItem
                    langId={content.LEFT}
                    imgSrc={`${STATIC_PATH}left-align.svg`}
                    hoverImgSrc={`${STATIC_PATH}left-align.svg`}
                    onClick={() => handleAlign("left")}
                />
                <ArrangeListItem
                    langId={content.MIDDLE}
                    imgSrc={`${STATIC_PATH}middle-align.svg`}
                    hoverImgSrc={`${STATIC_PATH}middle-align.svg`}
                    onClick={() => handleAlign("middle")}
                />
                <ArrangeListItem
                    langId={content.CENTER}
                    imgSrc={`${STATIC_PATH}center-align.svg`}
                    hoverImgSrc={`${STATIC_PATH}center-align.svg`}
                    onClick={() => handleAlign("center")}
                />
                <ArrangeListItem
                    langId={content.BOTTOM}
                    imgSrc={`${STATIC_PATH}bottom-align.svg`}
                    hoverImgSrc={`${STATIC_PATH}bottom-align.svg`}
                    onClick={() => handleAlign("bottom")}
                />
                <ArrangeListItem
                    langId={content.RIGHT}
                    imgSrc={`${STATIC_PATH}right-align.svg`}
                    hoverImgSrc={`${STATIC_PATH}right-align.svg`}
                    onClick={() => handleAlign("right")}
                />

                {/* space evenly menu */}
                <ArrangeListHeading>
                    <p>{intl.formatMessage({ id: content.SPACE_EVENLY })}</p>
                </ArrangeListHeading>
                <ArrangeListItem
                    langId={content.VERTICALLY}
                    imgSrc={`${STATIC_PATH}vertically.svg`}
                    hoverImgSrc={`${STATIC_PATH}vertically.svg`}
                    onClick={() => handleAlign("vertically")}
                />
                <ArrangeListItem
                    langId={content.TIDY_UP}
                    imgSrc={`${STATIC_PATH}tidyup.svg`}
                    hoverImgSrc={`${STATIC_PATH}tidyup.svg`}
                    onClick={() => handleAlign("tidyup")}
                />
                <ArrangeListItem
                    langId={content.HORIZONTALLY}
                    imgSrc={`${STATIC_PATH}horizontally.svg`}
                    hoverImgSrc={`${STATIC_PATH}horizontally.svg`}
                    onClick={() => handleAlign("horizontally")}
                />
            </ul>
        </ArrangePopupStyled>
    );
}

const FlipSection = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const childrenSelection = useSelector(state => state.app.get("childrenSelection"));
    const _selectedItems = useSelector(state => state.app.get("selectedItems"));
    const selectedFrameClip = useSelector((state) => state.app.get("selectedFrameClip"));
    const projectDetails = useSelector((state) => state.projectDetails);
    const theme = useSelector((state) => state.app.get('theme'));

    const { selectionContainer, selectedItems } = useMemo(() => {
        return {
            selectionContainer: getSelectionContainer({
                childrenSelection,
                selectedItems: _selectedItems,
            }),
            selectedItems: getSelectedItemIds({
                childrenSelection,
                selectedItems: _selectedItems,
            }),
        };
    }, [
        childrenSelection,
        _selectedItems,
    ]);
    const firstItemId = selectedItems.first();
    const orgSelectedItem = projectDetails.getIn([selectionContainer, firstItemId]);

    const selectedItem = useMemo(() => {
        let item = orgSelectedItem;
        if (item.get("isSingleClipFrame")) {
            item = singleClipFrameToFrame(item);
        }
        return item;
    }, [orgSelectedItem]);

    const getFlipInfo = useCallback(() => {
        let flipItem = selectedItem;
        let isClipFlip = false;
        let flipState = {
            isHorizontalFlip: false,
            isVerticalFlip: false,
            isDisabled: true,
            shouldHide: false,
        };

        if (selectedItem && selectedFrameClip.get("clipId")) {
            const imgDetails = selectedItem.getIn(["clipDetails", selectedFrameClip.get("clipId"), "imgDetails"]);
            if (
                imgDetails
                && (isImageOnly(imgDetails.get("type"), imgDetails.get("subType")) || isVideoOnly(imgDetails.get("type"), imgDetails.get("subType")))
            ) {
                flipItem = imgDetails;
                isClipFlip = true;
            }
        } else if (selectedItem && (selectedItem.get("type") === "GROUP" || selectedItem.get("type") === "TEXT")) {
            flipItem = undefined;
            flipState.shouldHide = true;
        }

        if (flipItem) {
            flipState = flipPositionToState(flipItem.get("flipPosition"));
            flipState.isDisabled = false;
            flipState.shouldHide = false;
        }

        return {
            selectedItem,
            flipItem,
            isClipFlip,
            flipState,
        }
    }, [selectedItem, selectedFrameClip]);

    const flipState = useMemo(() => getFlipInfo().flipState, [getFlipInfo]);

    const handleFlip = (type = "hor") => {
        const flipInfo = getFlipInfo();
        const { isClipFlip, selectedItem, flipItem } = flipInfo;

        if (!flipItem) {
            return;
        }

        const newFlipState = { ...flipInfo.flipState };
        if (type === "hor") {
            newFlipState.isHorizontalFlip = !newFlipState.isHorizontalFlip;
        }
        if (type === "ver") {
            newFlipState.isVerticalFlip = !newFlipState.isVerticalFlip;
        }
        const flipPosition = flipStateToPosition(newFlipState);

        const toUpdate = [
            {
                container: selectionContainer,
                id: selectedItem.get("id"),
                data: { flipPosition },
                clipId: isClipFlip ? selectedFrameClip.get("clipId") : undefined,
            }
        ];
        dispatch(applyFlip(toUpdate));
    }

    if (flipState.shouldHide) {
        return null;
    }

    let horButtonCls = "";
    if (flipState.isHorizontalFlip) {
        horButtonCls = `${horButtonCls} is-flip-hor`;
    }
    let verButtonCls = "";
    if (flipState.isVerticalFlip) {
        verButtonCls = `${horButtonCls} is-flip-ver`;
    }

    return (
        <TransformFlipContainer>
            <TransformHeading>{intl.formatMessage({ id: content.FLIP })}</TransformHeading>
            <TransformButton
                className={horButtonCls}
                onClick={() => handleFlip("hor")}
            >
                <img draggable="false" alt="flip horizontally" src={`${STATIC_PATH}${vmTheme[theme].icons.flipSideIcon}`} />
            </TransformButton>
            <TransformButton
                className={verButtonCls}
                onClick={() => handleFlip("ver")}
            >
                <img draggable="false" alt="flip vertically" src={`${STATIC_PATH}${vmTheme[theme].icons.flipTopIcon}`} />
            </TransformButton>
        </TransformFlipContainer>
    );
}

const RotateSection = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const theme = useSelector((state) => state.app.get('theme'));
    const selectedItems = useSelector((state) => state.app.get("selectedItems"));
    const originalAngle = useSelector((state) => {
        let rotation = 0;
        const selectedItems = state.app.get("selectedItems");
        const childrenSelection = state.app.get("childrenSelection");
        const selectionContainer = getSelectionContainer({
            childrenSelection,
            selectedItems,
        });
        const clipId = state.app.getIn(["selectedFrameClip", "clipId"]);

        if (selectedItems.size === 1 && selectionContainer === "workspaceItems" && !clipId) {
            const id = selectedItems.first();
            rotation = state.projectDetails.getIn([selectionContainer, id, "angle"]);
        }

        return rotation;
    });
    const [angle, setAngle] = useState(() => rotationDisplayValue(Math.round(originalAngle)));
    const canUpdateState = useRef(false);
    const isMultipleSelection = selectedItems.size > 1;

    useEffect(() => {
        canUpdateState.current = true;
        dispatch(updateImperativeRotate({ reset: true }));
        return () => {
            canUpdateState.current = false;
            dispatch(updateImperativeRotate({ reset: true }))
        };
    }, [dispatch]);

    const onChange = (e, newRotation) => setAngle(Math.round(newRotation));

    const onSubmit = () => {
        if (isMultipleSelection && canUpdateState.current) {
            setAngle(0);
        }
        const stateAngle = rotationDisplayValue(angle, true);
        if (originalAngle !== stateAngle) {
            dispatch(updateImperativeRotate({ rotateTo: angle }));
        }
    }

    const handleRotateButtonClick = (direction) => {
        const newRotation = getNextRotation({
            angle,
            direction,
        });
        dispatch(updateImperativeRotate({ rotateTo: newRotation }));
    }

    useEffect(() => {
        setAngle(rotationDisplayValue(Math.round(originalAngle)));
    }, [originalAngle]);

    const displayValue = Math.round(angle);
    return (
        <TransformRotateContainer>
            <TransformHeading className="rotate-heading">{intl.formatMessage({ id: content.ROTATE })}</TransformHeading>
            <NumberInput
                min={-180}
                max={180}
                step={1}
                value={displayValue}
                precision={0}
                onChange={onChange}
                onSubmit={onSubmit}
                suffix="°"
                saveOnUnmount={false}
                draggable
                dragStepPercent={0.05}
            />
            <TransformButton onClick={() => handleRotateButtonClick("anticlockwise")}>
                <img draggable="false" alt="rotate clockwise" src={`${STATIC_PATH}${vmTheme[theme].icons.rotateAntiClockWise}`} />
            </TransformButton>
            <TransformButton onClick={() => handleRotateButtonClick("clockwise")}>
                <img draggable="false" alt="rotate anticlockwise" src={`${STATIC_PATH}${vmTheme[theme].icons.rotateClockwise}`} />
            </TransformButton>
        </TransformRotateContainer>
    )
}

export const TransformPopup = () => {
    const selectedItems = useSelector((state) => state.app.get("selectedItems"));
    const childrenSelection = useSelector(state => state.app.get("childrenSelection"));
    const selectedFrameClip = useSelector((state) => state.app.get("selectedFrameClip"));

    const selectionContainer = useMemo(() => {
        return getSelectionContainer({
            childrenSelection,
            selectedItems,
        });
    }, [
        childrenSelection,
        selectedItems,
    ]);

    let flip = null;
    if (selectedItems.size === 1) {
        flip = <FlipSection />;
    }

    let rotate = null;
    if (selectedItems.size > 0 && selectionContainer === "workspaceItems" && !selectedFrameClip.get("clipId")) {
        rotate = <RotateSection />
    }

    return (
        <TransformPopupStyled>
            {flip}
            {rotate}
        </TransformPopupStyled>
    );
}

export const RadiusPopup = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const zoomFactor = useSelector((state) => state.app.get("zoomFactor"));
    const childrenSelection = useSelector(state => state.app.get("childrenSelection"));
    const _selectedItems = useSelector(state => state.app.get("selectedItems"));
    const projectDetails = useSelector((state) => state.projectDetails);

    const { selectionContainer, selectedItems } = useMemo(() => {
        return {
            selectionContainer: getSelectionContainer({
                childrenSelection,
                selectedItems: _selectedItems,
            }),
            selectedItems: getSelectedItemIds({
                childrenSelection,
                selectedItems: _selectedItems,
            }),
        };
    }, [
        childrenSelection,
        _selectedItems,
    ]);
    const firstItemId = selectedItems.first();
    const selectedItem = projectDetails.getIn([selectionContainer, firstItemId]);

    const [radius, setRadius] = useState(
        () => fromJS({
            tl: 0,
            tr: 0,
            br: 0,
            bl: 0,
            linked: true,
        })
    );
    const [item, setItem] = useState(selectedItem);

    useEffect(() => {
        let _radius = fromJS({
            tl: 0,
            tr: 0,
            br: 0,
            bl: 0,
            linked: true,
        });
        if (selectedItem && selectedItem.get("radius")) {
            _radius = selectedItem.get("radius");
        }
        setRadius(_radius);
        // to delay selection change for submit on unmount
        setItem(selectedItem);
    }, [selectedItem]);

    const applyRadiusClipPath = (item) => {
        const itemId = item.get("id");
        const clipEl = document.getElementById(itemId);
        if (clipEl) {
            let clipPath = getRadiusClipPath(item, zoomFactor);
            clipPath = `path("${clipPath}")`;
            clipEl.style.clipPath = clipPath;
        }
    }

    /**
     * 
     * @param {Event} e
     * @param {number} newPercent
     * @param {"tl" | "tr" | "br" | "bl"} type 
     */
    const onChange = (e, newPercent, type) => {
        let newRadiusState = radius;

        const cornerKeys = ["tl", "tr", "br", "bl"];
        if (newRadiusState.get("linked")) {
            cornerKeys.forEach((corner) => {
                newRadiusState = newRadiusState.set(corner, newPercent);
            });
        } else if (cornerKeys.includes(type)) {
            newRadiusState = newRadiusState.set(type, newPercent);
        }

        if (newRadiusState !== radius) {
            if (item) {
                const radiusItem = item.set("radius", newRadiusState);
                applyRadiusClipPath(radiusItem);
            }
            setRadius(newRadiusState);
        }
    }

    const onSubmit = () => {
        if (radius !== item.get("radius")) {
            const toUpdate = {
                [item.get("id")]: {
                    data: {
                        radius,
                    },
                }
            };
            if (item && item.get("id")) {
                dispatch(bulkObjectUpdate({ workspaceItems: toUpdate }));
            }
        }
    }

    const toggleLink = () => {
        const newRadius = radius.set("linked", !radius.get("linked"));
        const toUpdate = {
            [selectedItem.get("id")]: {
                data: {
                    radius: newRadius,
                },
            }
        };
        dispatch(bulkObjectUpdate({ workspaceItems: toUpdate }));
    }

    let radiusIcon;
    if (radius.get("linked")) {
        radiusIcon = (
            <RadiusLink className="linked" onClick={toggleLink}>
                <img draggable="false" alt="unlink borders" src={`${STATIC_PATH}radius-linked.svg`} />
            </RadiusLink>
        );
    } else {
        radiusIcon = (
            <RadiusLink className="unlinked" onClick={toggleLink}>
                <img draggable="false" alt="link borders" src={`${STATIC_PATH}radius-unlinked.svg`} />
            </RadiusLink>
        );
    }

    return (
        <RadiusPopupStyled>
            <p>{intl.formatMessage({ id: content.RADIUS })}</p>
            <div>
                <NumberInput
                    min={0}
                    max={100}
                    step={1}
                    value={radius.get("tl")}
                    precision={0}
                    onChange={(e, v) => onChange(e, v, "tl")}
                    onSubmit={onSubmit}
                    suffix=""
                    saveOnUnmount={true}
                    draggable
                />
                <NumberInput
                    min={0}
                    max={100}
                    step={1}
                    value={radius.get("tr")}
                    precision={0}
                    onChange={(e, v) => onChange(e, v, "tr")}
                    onSubmit={onSubmit}
                    suffix=""
                    saveOnUnmount={false}
                    draggable
                />
                <NumberInput
                    min={0}
                    max={100}
                    step={1}
                    value={radius.get("bl")}
                    precision={0}
                    onChange={(e, v) => onChange(e, v, "bl")}
                    onSubmit={onSubmit}
                    suffix=""
                    saveOnUnmount={true}
                    draggable
                />
                <NumberInput
                    min={0}
                    max={100}
                    step={1}
                    value={radius.get("br")}
                    precision={0}
                    onChange={(e, v) => onChange(e, v, "br")}
                    onSubmit={onSubmit}
                    suffix=""
                    saveOnUnmount={true}
                    draggable
                />
                {radiusIcon}
            </div>
        </RadiusPopupStyled>
    );
}
